import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatCurrencyCompact, formatMonth } from '../../lib/format.js';
import { cn } from '@tailoring-crm/ui';
import { ChartViewToggle, type ChartView } from '../reports/report-chart.js';
import { AnalyticsPanel } from './analytics-panel.js';
import type { DailySeries } from './sales-series.js';
import { SALES_TONE, TONE_CLASSES } from './visuals.js';

/**
 * This month's sales day by day, laid over last month's.
 *
 * The two months are lined up by day-of-month, so the owner reads "are we ahead of where we were
 * on the 12th last month?" straight off the picture. This month is the filled shape in the sales
 * colour and stops at today; last month is a dashed slate line across the whole width — present
 * enough to compare against, quiet enough not to be mistaken for the month that is happening.
 *
 * The drawing stretches with `preserveAspectRatio="none"`; its lines keep a constant thickness
 * through `vector-effect: non-scaling-stroke`, so a phone does not draw them as hairlines and a
 * wide screen does not draw them as ribbons. Axis labels are HTML, positioned by percentage, so
 * they never stretch with it. The table underneath is the data, as everywhere (see
 * `report-chart.tsx`).
 */

const WIDTH = 300;
const HEIGHT = 120;
/** Headroom above the busiest day, so its peak is not clipped by the edge of the drawing. */
const TOP_PAD = 6;

function xOf(index: number, axisDays: number): number {
  return axisDays <= 1 ? 0 : (index / (axisDays - 1)) * WIDTH;
}

function yOf(value: number, max: number): number {
  return max === 0 ? HEIGHT : HEIGHT - (value / max) * (HEIGHT - TOP_PAD);
}

function linePath(values: readonly number[], axisDays: number, max: number): string {
  return values
    .map((value, index) => {
      const command = index === 0 ? 'M' : 'L';
      return `${command}${xOf(index, axisDays).toFixed(2)},${yOf(value, max).toFixed(2)}`;
    })
    .join(' ');
}

function areaPath(values: readonly number[], axisDays: number, max: number): string {
  if (values.length === 0) return '';
  const lastX = xOf(values.length - 1, axisDays).toFixed(2);
  return `${linePath(values, axisDays, max)} L${lastX},${String(HEIGHT)} L0,${String(HEIGHT)} Z`;
}

/** The days the x-axis names: the 1st, every seventh day after, and the month's end. */
function axisTicks(axisDays: number): number[] {
  const ticks = [1, 8, 15, 22].filter((day) => day < axisDays - 2);
  return [...ticks, axisDays];
}

export function DailySalesTrend({
  series,
  className,
}: {
  series: DailySeries;
  className?: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const [view, setView] = useState<ChartView>('chart');
  const title = t('dashboard.analytics.dailySales');
  const { thisMonth, lastMonth, axisDays } = series;

  const max = Math.max(0, ...thisMonth, ...lastMonth);
  const empty = max === 0;
  const showChart = view === 'chart' && !empty;
  const thisLabel = `${t('dashboard.analytics.thisMonth')} (${formatMonth(series.thisMonthKey)})`;
  const lastLabel = `${t('dashboard.analytics.lastMonth')} (${formatMonth(series.lastMonthKey)})`;
  const tone = TONE_CLASSES[SALES_TONE];

  return (
    <AnalyticsPanel
      title={title}
      className={className}
      actions={<ChartViewToggle view={view} onChange={setView} />}
    >
      {empty ? <p className="text-sm text-muted-foreground">{t('reports.noData')}</p> : null}

      {showChart ? (
        <div aria-hidden="true" className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className={cn('h-0.5 w-4 rounded-full', tone.bar)} />
              {thisLabel}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 border-t-2 border-dashed border-tone-slate" />
              {lastLabel}
            </span>
          </div>

          <div className="flex gap-2">
            {/* The scale, beside the drawing and exactly as tall, so its ends sit on the lines. */}
            <div className="flex h-36 shrink-0 flex-col justify-between text-right text-[11px] text-muted-foreground tabular-nums">
              <span>{formatCurrencyCompact(max)}</span>
              <span>{formatCurrencyCompact(0)}</span>
            </div>
            <div className="min-w-0 flex-1">
              <svg
                viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
                preserveAspectRatio="none"
                className="h-36 w-full overflow-visible"
                data-testid="daily-trend"
              >
                {[0, 0.5, 1].map((share) => (
                  <line
                    key={share}
                    x1="0"
                    x2={WIDTH}
                    y1={HEIGHT - share * (HEIGHT - TOP_PAD)}
                    y2={HEIGHT - share * (HEIGHT - TOP_PAD)}
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                    className="stroke-border"
                  />
                ))}
                <path
                  d={linePath(lastMonth, axisDays, max)}
                  fill="none"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                  className={TONE_CLASSES.slate.stroke}
                  data-series="last-month"
                />
                <path
                  d={areaPath(thisMonth, axisDays, max)}
                  fillOpacity={0.15}
                  className={tone.fill}
                  data-series="this-month-area"
                />
                <path
                  d={linePath(thisMonth, axisDays, max)}
                  fill="none"
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  className={tone.stroke}
                  data-series="this-month"
                />
              </svg>
              <div className="relative mt-1 h-4 text-[11px] text-muted-foreground tabular-nums">
                {axisTicks(axisDays).map((day, index, all) => (
                  <span
                    key={day}
                    className={cn(
                      'absolute top-0',
                      index === 0
                        ? ''
                        : index === all.length - 1
                          ? '-translate-x-full'
                          : '-translate-x-1/2',
                    )}
                    style={{ left: `${(xOf(day - 1, axisDays) / WIDTH) * 100}%` }}
                  >
                    {day}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Scrolls inside its own box when shown, so thirty-one rows do not stretch the grid. */}
      <div className={cn(showChart ? 'sr-only' : 'max-h-72 overflow-y-auto')}>
        <table className="w-full text-sm">
          <caption className="sr-only">{title}</caption>
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th scope="col" className="py-1 font-medium">
                {t('dashboard.analytics.day')}
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                {thisLabel}
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                {lastLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: axisDays }, (_, index) => (
              <tr key={index} className="border-t border-border">
                <th scope="row" className="py-1.5 text-left font-normal tabular-nums">
                  {index + 1}
                </th>
                <td className="py-1.5 text-right tabular-nums">
                  {index < thisMonth.length
                    ? formatCurrency(thisMonth[index]!, { compact: true })
                    : '—'}
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  {index < lastMonth.length
                    ? formatCurrency(lastMonth[index]!, { compact: true })
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AnalyticsPanel>
  );
}
